import { ContentHelper, ContentInput } from './contentHelper';

export class ClipboardHelper extends ContentHelper {
  private static readonly INSTANCE = new ClipboardHelper();

  private constructor() {
    super();
  }

  public static get isAvailable(): boolean {
    return this.INSTANCE.isAvailable();
  }

  public static getInstance() {
    return this.INSTANCE;
  }

  public copyText(text: string): Promise<ClipboardItem> {
    try {
      const clipboardItem = this.createClipboardItem(text);
      return navigator.clipboard
        .write([clipboardItem])
        .then(() => clipboardItem);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  /**
   * Copies text that is not known yet — typically the answer to a request the click itself
   * started. WebKit ties `navigator.clipboard.write` to the click's transient activation, which
   * an `await` has already spent by the time the answer arrives; the spec's way out is a
   * `ClipboardItem` holding a *promise* of the blob, which is what this builds. It therefore
   * has to be called synchronously from the event handler, exactly like {@link copyText}.
   */
  public copyTextAsync(text: Promise<string>): Promise<ClipboardItem> {
    try {
      const clipboardItem = new ClipboardItem({
        'text/plain': text.then(
          (resolved) => new Blob([resolved], { type: 'text/plain' })
        )
      });
      return navigator.clipboard
        .write([clipboardItem])
        .then(() => clipboardItem);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  public copyElementContent(
    elementOrId: ContentInput,
    includeFormatted = true
  ): Promise<ClipboardItem> {
    try {
      const element = this.getNode(elementOrId);
      const clipboardItem = this.createClipboardItem(element, includeFormatted);
      return navigator.clipboard
        .write([clipboardItem])
        .then(() => clipboardItem);
    } catch (err) {
      return Promise.reject(err);
    }
  }

  protected isAvailable(): boolean {
    return this.isClipboardSupported();
  }

  private createClipboardItem(
    el: NonNullable<string | Element>,
    includeFormatted = true
  ): ClipboardItem {
    if (typeof el === 'string') {
      const blob = new Blob([el], { type: 'text/plain' });
      return new ClipboardItem({ 'text/plain': blob });
    }
    const htmlBlob =
      includeFormatted && new Blob([el.innerHTML], { type: 'text/html' });
    const textBlob = new Blob([this.getTextFromNode(el)], {
      type: 'text/plain'
    });
    if (htmlBlob) {
      return new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      });
    }

    return new ClipboardItem({
      'text/plain': textBlob
    });
  }

  private isClipboardSupported() {
    return (
      typeof window !== 'undefined' &&
      typeof window.ClipboardItem !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      typeof navigator.clipboard !== 'undefined'
    );
  }
}
